import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DialogService } from '../services/dialog-service/dialog-service.service';
import { ErrorToDialog } from '../model/error/error-to-dialog';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { REQUEST } from '@angular/core';
import { getLocalStorageItem } from 'src/app/utils/browser-storage';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    private dialogService: DialogService,
    @Inject(PLATFORM_ID) platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.withServerOrigin(req);

    // custom Header defined in userservice to stop redirect after failed password change
    const skip401Redirect = req.headers.has('X-Skip-401-Redirect');
    if (skip401Redirect) {
        req = req.clone({ headers: req.headers.delete('X-Skip-401-Redirect') });
    }

    if (!req.url.endsWith('/health')) req = this.addAuthenticationToken(req);
    const requestWithCredentials = req.clone({ withCredentials: true });

    return next.handle(requestWithCredentials).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.error) {
          let parsed: ErrorToDialog | null = null;

          if (typeof error.error === 'object' && 'title' in error.error) {
            parsed = error.error;
          } else if (
            error.error.message &&
            (error.error.message as string).includes('title')
          ) {
            parsed = JSON.parse(error.error.message);
          }

          if (parsed) {
            if (this.isBrowser) {
              this.dialogService.openDialog(parsed.title, parsed.message);
            }
          }
        }

        if (error.status === 401 && !skip401Redirect && !this.shouldSuppress401Redirect(req.url)) {
          if (this.isBrowser) {
            this.router.navigateByUrl('/marktplatz?tab=0');
          }
          return throwError(error);
        }

        return throwError(error);
      })
    );
  }

  private shouldSuppress401Redirect(url: string): boolean {
      const browserPath = this.isBrowser ? window.location.pathname : '';
      return (
          url.includes('/userservice/users/login/anonymous') ||
          url.includes('/userservice/users/anon/permissions') ||
          url.includes('/proxy/api/v0/login') ||
          url.includes('/productservice/issuer-certifications/') ||
          (this.isBrowser && (
              this.router.url.startsWith('/marktplatz/products/') ||
              browserPath.startsWith('/marktplatz/products/')
          ))
      );
  }

  private withServerOrigin(request: HttpRequest<any>) {
      if (
          this.isBrowser ||
          !this.request ||
          /^https?:\/\//i.test(request.url) ||
          request.url.startsWith('//')
      ) {
          return request;
      }

      try {
          return request.clone({
              url: new URL(request.url, this.request.url).toString()
          });
      } catch (_error) {
          return request;
      }
  }

  addAuthenticationToken(request) {
        const access_token = getLocalStorageItem('access_token');

        if (access_token && !request.headers.has('Authorization')) {
            const headers: HttpHeaders = request.headers.set(
                'Authorization',
                'Bearer ' + access_token
            );
            request = request.clone({ headers: headers });
        }
        return request;
    }
}
